import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { get, post, put, ApiError } from '@/lib/api';
import { LogoZoneEditor } from './LogoZoneEditor';
import { PrizeZoneEditor } from './PrizeZoneEditor';
import {
  type PrizeProduct, type Zone, type PrintArea, type MockupView, type MockupViewName,
  type DecorationRegistry, type DecorationLocation,
  previewSku, clientValidate, blankPrizeProduct, slugColor,
  placementOptions, derivePrintAreas,
} from '@/lib/prize-product';
import { MAX_PRIZE_TIERS, prizeIndexes } from '@/lib/prize-tiers';
import { VIEW_LABELS, initialView, overlappingZones, zonesMissingImage } from '@/lib/prize-zones';

type Mode = 'new' | 'edit' | 'duplicate';
type Props = { open: boolean; mode: Mode; seed?: PrizeProduct | null; onOpenChange: (o: boolean) => void; onSaved: () => void };
type MockupsPayload = { files: string[] };

function seedDraft(mode: Mode, seed?: PrizeProduct | null): PrizeProduct {
  if (mode === 'new' || !seed) return blankPrizeProduct();
  const copy = structuredClone(seed);
  // `_rev` is the stored document's version, carried through an edit so the
  // server can refuse a save based on a copy someone else has since changed.
  // A duplicate is a NEW document and has no version yet, so drop it.
  if (mode === 'duplicate') { copy.id = ''; copy.displayName = `${seed.displayName} (copy)`; delete copy._rev; }
  copy.mockup = Object.assign({ logoPosition: { x: 0, y: 0 }, logoMaxWidth: 100, logoMaxHeight: 100, baseImage: '' }, copy.mockup);
  copy.shopify = Object.assign({ sizes: [] as string[] }, copy.shopify);
  copy.tiers = prizeIndexes(MAX_PRIZE_TIERS);
  return copy;
}

export function PrizeProductDialog({ open, mode, seed, onOpenChange, onSaved }: Props) {
  const [draft, setDraft] = useState<PrizeProduct>(() => seedDraft(mode, seed));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [baseBusy, setBaseBusy] = useState(false);
  const [baseMsg, setBaseMsg] = useState<string | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [zoneView, setZoneView] = useState<MockupViewName>('front');
  const { data: mockups, mutate: revalidateMockups } = useSWR<MockupsPayload>(open ? '/api/prize-products/mockups' : null, get);
  // Decoration location registry (platformConfig/decorationLocations). The
  // route 503s when the doc is missing rather than serving a fallback map, so
  // an error here means "seed the registry", not "pick from a default list".
  const { data: registryData, error: registryErr } = useSWR<{ locations: Record<string, DecorationLocation> }>(
    open ? '/api/decoration-locations' : null, get);
  const registry: DecorationRegistry | undefined = registryData ? { locations: registryData.locations } : undefined;
  const registryProblem = registryErr
    ? ((registryErr as ApiError).body as { message?: string } | null)?.message || 'Decoration locations unavailable.'
    : null;

  const styleCode = draft.sku?.styleCode || '';
  const colorCode = draft.sku?.colorCode || '';
  const baseCheckKey = open && styleCode.trim() && colorCode.trim()
    ? `/api/prize-products/base-check?style=${encodeURIComponent(styleCode)}&color=${encodeURIComponent(colorCode)}`
    : null;
  const { data: baseCheck, mutate: revalidateBase } = useSWR<{ exists: boolean; key: string; baseSizes?: string[] }>(baseCheckKey, get);
  const productSizes = draft.shopify?.sizes || [];
  const missingSizes = baseCheck?.exists ? productSizes.filter((s) => !(baseCheck.baseSizes || []).includes(s)) : [];
  const baseOk = !!baseCheck?.exists && missingSizes.length === 0;

  useEffect(() => {
    if (!open) return;
    const next = seedDraft(mode, seed);
    setDraft(next);
    setErr(null);
    setBaseMsg(null);
    // Open on the tab that already carries a zone so an existing garment lands
    // on its own artwork instead of an empty Front.
    setZoneView(initialView(next.printAreas || {}, next.mockupViews || {}));
  }, [open, mode, seed]);

  const set = (patch: Partial<PrizeProduct>) => setDraft((d) => ({ ...d, ...patch }));
  const setMockup = (patch: Partial<PrizeProduct['mockup']>) => setDraft((d) => ({ ...d, mockup: { ...d.mockup, ...patch } }));
  const setSku = (patch: Partial<NonNullable<PrizeProduct['sku']>>) => setDraft((d) => ({ ...d, sku: { ...d.sku, ...patch } }));
  const setProd = (patch: Partial<NonNullable<PrizeProduct['production']>>) => setDraft((d) => ({ ...d, production: { ...d.production, ...patch } }));
  const setShop = (patch: Partial<PrizeProduct['shopify']>) => setDraft((d) => ({ ...d, shopify: { ...d.shopify, ...patch } }));

  // Registry-driven placement list; a new product adopts the first alias once
  // the registry lands so no location string is hardcoded client-side.
  const placementOpts = useMemo(() => placementOptions(registry), [registry]);
  useEffect(() => {
    if (!open || !placementOpts.length || draft.placement) return;
    if (draft.printAreas && Object.keys(draft.printAreas).length) return;
    set({ placement: placementOpts[0][0] });
  }, [open, placementOpts, draft.placement, draft.printAreas]);

  const skuPreview = useMemo(() => {
    if (!registry) return registryProblem || 'Loading decoration locations…';
    try { return previewSku(draft, registry); } catch (e) { return e instanceof Error ? e.message : 'SKU unavailable.'; }
  }, [draft, registry, registryProblem]);

  const mockupOpts: [string, string][] = (mockups?.files || []).map((f) => [f, f]);

  // A zone on a view with no uploaded image cannot be proofed (the server
  // refuses the same shape in lib/prize-product.js checkArea). Block the save
  // here so the operator sees it in the editor, not as a 400 after the fact.
  const zoneProblems = useMemo(
    () => zonesMissingImage(draft.printAreas || {}, draft.mockupViews || {}),
    [draft.printAreas, draft.mockupViews],
  );

  // Two zones that could be ordered TOGETHER, drawn on top of each other. A
  // CONFLICTING pair overlapping is fine and expected (FF sits over LC/RC on
  // every top we sell), so only non-conflicting overlaps land here. The server
  // rejects the same shape in lib/prize-product.js overlappingZones.
  const zoneOverlaps = useMemo(
    () => overlappingZones(draft.printAreas || {}, registry),
    [draft.printAreas, registry],
  );
  // A zone whose image NAME is set but whose image does not actually load. Only
  // the editor can know this (it is the thing rendering the <img>), and
  // zonesMissingImage cannot see it because the filename string is present — so
  // without this the operator could save a product whose proof composites onto
  // nothing. Reported up from PrizeZoneEditor; see Codex F10.
  const [brokenImages, setBrokenImages] = useState<{ code: string; view: MockupViewName }[]>([]);
  useEffect(() => { if (!open) setBrokenImages([]); }, [open]);

  const saveBlocked = zoneProblems.length > 0 || zoneOverlaps.length > 0 || brokenImages.length > 0;

  async function save() {
    const problem = clientValidate(draft, registry);
    if (problem) { setErr(problem); return; }
    setBusy(true); setErr(null);
    try {
      if (mode === 'edit') await put(`/api/prize-products/${encodeURIComponent(draft.id)}`, draft);
      else await post('/api/prize-products', draft);
      onSaved();
      onOpenChange(false);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { message?: string; error?: string } | null;
        setErr(body?.message || body?.error || `Save failed (${e.status}).`);
      } else { setErr('Save failed.'); }
    } finally { setBusy(false); }
  }

  async function createBase() {
    setBaseBusy(true); setBaseMsg(null);
    try {
      const r = await post<{ success: boolean; alreadyExisted?: boolean; masterKey?: string; addedSizes?: string[] }>('/api/prize-products/create-base', {
        styleNumber: styleCode, colorCode, color: draft.color || colorCode,
        brand: draft.shopify?.vendor || 'GhostFit',
        productType: draft.shopify?.productType || draft.garment || '',
        sizes: productSizes, basePrice: draft.shopify?.basePrice || '0',
        vendor: draft.shopify?.vendor || 'Ghost Fit Apparel',
      });
      setBaseMsg(
        r.addedSizes && r.addedSizes.length ? `Added ${r.addedSizes.join(', ')} to base ${r.masterKey}.`
          : r.alreadyExisted ? `Base already exists (${r.masterKey}).` : `Base product created (${r.masterKey}).`
      );
      void revalidateBase();
    } catch (e) {
      const b = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null;
      setBaseMsg(b?.message || b?.error || 'Create base failed.');
    } finally { setBaseBusy(false); }
  }

  async function uploadMockup(file: File) {
    setUploadBusy(true); setErr(null);
    try {
      const dims = await new Promise<{ w: number; h: number } | null>((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ w: img.naturalWidth, h: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
      });
      if (!dims) { setErr('Could not read that image.'); return; }
      if (dims.w < 500 || dims.h < 500) { setErr(`Mockup too small (${dims.w}×${dims.h}px). Use at least 500×500.`); return; }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('filename', file.name);
      const res = await fetch('/api/prize-products/mockups', { method: 'POST', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
      const body = await res.json().catch(() => ({} as { filename?: string; message?: string; error?: string }));
      if (!res.ok || !body.filename) { setErr(body.message || body.error || `Upload failed (${res.status}).`); return; }
      await revalidateMockups();
      setMockup({ baseImage: body.filename });
    } catch { setErr('Upload failed.'); } finally { setUploadBusy(false); }
  }

  async function deleteMockup() {
    const file = draft.mockup.baseImage;
    if (!file) return;
    if (!confirm(`Delete mockup "${file}" from the library? (Blocked if any product uses it.)`)) return;
    setUploadBusy(true); setErr(null);
    try {
      const res = await fetch(`/api/prize-products/mockups/${encodeURIComponent(file)}`, { method: 'DELETE', credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
      const body = await res.json().catch(() => ({} as { success?: boolean; message?: string; error?: string }));
      if (!res.ok || !body.success) { setErr(body.message || body.error || `Delete failed (${res.status}).`); return; }
      await revalidateMockups();
      setMockup({ baseImage: '' });
    } catch { setErr('Delete failed.'); } finally { setUploadBusy(false); }
  }

  const zone: Zone = {
    x: draft.mockup.logoPosition?.x ?? 0,
    y: draft.mockup.logoPosition?.y ?? 0,
    logoMaxWidth: draft.mockup.logoMaxWidth ?? 100,
    logoMaxHeight: draft.mockup.logoMaxHeight ?? 100,
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit' : mode === 'duplicate' ? 'Duplicate' : 'New'} prize product</DialogTitle>
          <DialogDescription>
            Writes the <code>prizeProducts</code> catalog. New products appear in the New Submission dropdowns within ~60s.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 text-[12px]">
          <Section title="Identity">
            <Grid>
              <Text label="ID (kebab-case)" value={draft.id} disabled={mode === 'edit'} onChange={(v) => set({ id: v })} placeholder="navy-hoodie-left-chest" />
              <Text label="Display name" value={draft.displayName} onChange={(v) => set({ displayName: v })} placeholder="Navy Hoodie - Left Chest" />
              <Text label="Short name" value={draft.shortName || ''} onChange={(v) => set({ shortName: v })} placeholder="Navy Hoodie (LC)" />
              <label className="block">
                <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">Tiers</span>
                <div className="mt-1 w-full rounded-sm border border-border bg-bg px-2 py-1.5 text-[12px] text-muted">Tiers 1–{MAX_PRIZE_TIERS} (all prize products)</div>
              </label>
              <Select label="Active" value={draft.active === false ? 'no' : 'yes'} onChange={(v) => set({ active: v === 'yes' })} options={[['yes', 'Active'], ['no', 'Inactive']]} />
            </Grid>
          </Section>

          <Section title="Garment & SKU">
            <Grid>
              <Text label="Garment" value={draft.garment || ''} onChange={(v) => set({ garment: v })} placeholder="hoodie" />
              <Text label="Color" value={draft.color || ''} onChange={(v) => set({ color: v })} placeholder="navy" />
              <Select label="Placement (legacy)" value={draft.placement || ''} onChange={(v) => set({ placement: v })}
                options={placementOpts.length ? placementOpts : [[draft.placement || '', draft.placement || '— loading —']]} />
              <Text label="SKU style code" value={draft.sku?.styleCode || ''} onChange={(v) => setSku({ styleCode: v })} placeholder="GSF101" />
              <Text label="SKU color code" value={draft.sku?.colorCode || ''} onChange={(v) => setSku({ colorCode: v })} placeholder="navy" />
            </Grid>
            <p className="mt-2 font-mono text-[11px] text-muted">SKU preview: <span className="text-text">{skuPreview}</span></p>
          </Section>

          <Section title="Mockup & logo placement">
            <Grid>
              <Select label="Base image" value={draft.mockup.baseImage} onChange={(v) => setMockup({ baseImage: v })} options={[['', '— pick —'], ...mockupOpts]} />
              <Select label="Overlay (optional)" value={draft.mockup.overlayImage || ''} onChange={(v) => setMockup({ overlayImage: v || undefined })} options={[['', '— none —'], ...mockupOpts]} />
            </Grid>
            <div className="mt-2 flex items-center gap-2">
              <label className="cursor-pointer rounded-sm border border-border bg-surface px-2 py-1 text-[11px] hover:bg-bg">
                {uploadBusy ? 'Working…' : 'Upload new mockup…'}
                <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={uploadBusy}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadMockup(f); e.currentTarget.value = ''; }} />
              </label>
              {draft.mockup.baseImage && (
                <button type="button" disabled={uploadBusy} onClick={() => void deleteMockup()} className="rounded-sm border border-border px-2 py-1 text-[11px] text-red-600 hover:bg-bg disabled:opacity-60">Delete selected</button>
              )}
              <span className="text-[10px] text-muted">PNG/JPG/WebP, ≥500px, ≤8 MB.</span>
            </div>
            <div className="mt-3">
              {draft.mockup.baseImage
                ? <LogoZoneEditor baseImage={draft.mockup.baseImage} value={zone} onChange={(z) => setMockup({ logoPosition: { x: z.x, y: z.y }, logoMaxWidth: z.logoMaxWidth, logoMaxHeight: z.logoMaxHeight })} />
                : <p className="text-muted">Pick a base image to position the logo zone.</p>}
            </div>
          </Section>

          <Section title="Production (print size)">
            <Grid>
              <Num label="Width (in)" value={draft.production?.width} onChange={(v) => setProd({ width: v })} />
              <Num label="Height (in)" value={draft.production?.height} onChange={(v) => setProd({ height: v })} />
              <Num label="DPI" value={draft.production?.dpi} onChange={(v) => setProd({ dpi: v })} />
            </Grid>
          </Section>

          <Section title="Print zones">
            {registryProblem
              ? <p className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-700">{registryProblem}</p>
              : !registry
                ? <p className="text-[11px] text-muted">Loading decoration locations…</p>
                : Object.keys(draft.printAreas || {}).length === 0
                  ? (
                    <ConvertToPrintAreas
                      draft={draft} registry={registry}
                      onConvert={(printAreas, mockupViews) => {
                        set({ printAreas, mockupViews });
                        setZoneView(initialView(printAreas, mockupViews));
                      }}
                    />
                  )
                  : (
                    <PrizeZoneEditor
                      registry={registry}
                      productId={draft.id}
                      printAreas={draft.printAreas || {}}
                      mockupViews={draft.mockupViews || {}}
                      mockupFiles={mockups?.files || []}
                      defaultInches={{ width: draft.production?.width ?? 3.2, height: draft.production?.height ?? 3.2 }}
                      activeView={zoneView}
                      onActiveViewChange={setZoneView}
                      onChange={set}
                      onUploaded={() => { void revalidateMockups(); }}
                      onBrokenImages={setBrokenImages}
                    />
                  )}
          </Section>

          <Section title="Shopify">
            <Grid>
              <Text label="Title prefix" value={draft.shopify.titlePrefix || ''} onChange={(v) => setShop({ titlePrefix: v })} placeholder="Navy Hoodie" />
              <Text label="Product type" value={draft.shopify.productType || ''} onChange={(v) => setShop({ productType: v })} placeholder="Hoodie" />
              <Text label="Vendor" value={draft.shopify.vendor || ''} onChange={(v) => setShop({ vendor: v })} />
              <Text label="Base price" value={draft.shopify.basePrice || ''} onChange={(v) => setShop({ basePrice: v })} placeholder="54.50" />
              <Text label="Compare-at price" value={draft.shopify.compareAtPrice || ''} onChange={(v) => setShop({ compareAtPrice: v })} placeholder="98.00" />
              <Text label="SKU prefix (legacy)" value={draft.shopify.skuPrefix || ''} onChange={(v) => setShop({ skuPrefix: v })} placeholder="Pri-Hoo" />
              <Text label="SKU suffix (legacy)" value={draft.shopify.skuSuffix || ''} onChange={(v) => setShop({ skuSuffix: v })} placeholder="NVY" />
            </Grid>
            <Text label="Sizes (comma-separated)" value={(draft.shopify.sizes || []).join(', ')} onChange={(v) => setShop({ sizes: v.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="S, M, L, XL, 2XL" />
            <Text label="Tags (comma-separated)" value={(draft.shopify.tags || []).join(', ')} onChange={(v) => setShop({ tags: v.split(',').map((s) => s.trim()).filter(Boolean) })} placeholder="LE_TRACKING, GC, DTF_Order" />
            <label className="mt-2 block">
              <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">Body HTML</span>
              <textarea value={draft.shopify.bodyHtml || ''} onChange={(e) => setShop({ bodyHtml: e.target.value })} rows={3} className="mt-1 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px]" />
            </label>
          </Section>

          {styleCode.trim() && colorCode.trim() ? (
            <div className={`rounded-sm border px-3 py-2 text-[11px] ${baseOk ? 'border-emerald-300/60 bg-emerald-50 text-emerald-800' : 'border-amber-300/60 bg-amber-50 text-amber-800'}`}>
              {!baseCheck ? (
                <span className="text-muted">Checking base product for <code>{styleCode.toUpperCase()}-{slugColor(colorCode)}</code>…</span>
              ) : baseOk ? (
                <span>✓ Base product exists (<code>{baseCheck.key}</code>) — inventory will link on approval.</span>
              ) : baseCheck.exists ? (
                <div className="flex items-center justify-between gap-3">
                  <span>⚠️ Base <code>{baseCheck.key}</code> is missing sizes: <strong>{missingSizes.join(', ')}</strong> — those variants won't link.</span>
                  <Button variant="ghost" disabled={baseBusy} onClick={() => void createBase()} className="shrink-0">{baseBusy ? 'Adding…' : 'Add missing sizes'}</Button>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <span>⚠️ No base product for <code>{baseCheck.key}</code> — inventory won't link. Create the blank to enable it.</span>
                  <Button variant="ghost" disabled={baseBusy} onClick={() => void createBase()} className="shrink-0">{baseBusy ? 'Creating…' : 'Create base product'}</Button>
                </div>
              )}
              {baseMsg && <p className="mt-1 text-[10px] text-muted">{baseMsg}</p>}
            </div>
          ) : (
            <p className="rounded-sm border border-border bg-bg px-3 py-2 text-[11px] text-muted">Set the SKU style + color codes above to check the inventory base product.</p>
          )}

          {err && <p className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">{err}</p>}
        </div>

        <DialogFooter className="gap-2">
          {saveBlocked && (
            <span className="mr-auto text-[11px] text-red-600">
              Blocked: {[
                ...zoneProblems.map((p) => `${p.code} needs a ${VIEW_LABELS[p.view]} image`),
                ...brokenImages.map((b) => `${VIEW_LABELS[b.view]}'s image (used by ${b.code}) failed to load`),
                ...zoneOverlaps.map((o) => `${o.a} and ${o.b} overlap on ${VIEW_LABELS[o.view]}`),
              ].join('; ')}.
            </span>
          )}
          <Button variant="ghost" disabled={busy} onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={busy || saveBlocked} onClick={() => void save()}>{busy ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create product'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Legacy → print-areas migration
//
// A product with no `printAreas` still decorates: the pipeline DERIVES a single
// area from the legacy placement/production/mockup fields (F6,
// productconfig/lib/decoration.js derivePrintAreas). Show the operator exactly
// what production sees today, then "Convert to print areas" adopts it as real
// data — and the visual editor takes over with that zone already drawn.
// ---------------------------------------------------------------------------
function ConvertToPrintAreas({ draft, registry, onConvert }: {
  draft: PrizeProduct;
  registry: DecorationRegistry;
  onConvert: (printAreas: Record<string, PrintArea>, mockupViews: Record<string, MockupView>) => void;
}) {
  let derived: { printAreas: Record<string, PrintArea>; mockupViews: Record<string, MockupView> } | null = null;
  let derivedErr: string | null = null;
  try { derived = derivePrintAreas(draft, registry); } catch (e) { derivedErr = e instanceof Error ? e.message : 'Cannot derive print areas.'; }
  return (
    <div className="space-y-2">
      <p className="text-[11px] text-muted">
        No print zones configured. Production currently derives ONE zone from the legacy Placement / Production / Mockup fields above:
      </p>
      {derivedErr ? (
        <p className="rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-700">{derivedErr}</p>
      ) : (
        <div className="rounded-sm border border-border bg-bg px-3 py-2 font-mono text-[11px] text-muted">
          {Object.entries(derived!.printAreas).map(([code, area]) => (
            <div key={code}>
              [{code}] {registry.locations[code]?.label} — {area.widthInches ?? '?'}×{area.heightInches ?? '?'} in
              {area.mockup ? ` · ${area.mockup.view} @ ${area.mockup.x},${area.mockup.y} (${area.mockup.maxW}×${area.mockup.maxH}px)` : ''}
            </div>
          ))}
          {Object.entries(derived!.mockupViews).map(([view, v]) => (
            <div key={view}>{view}: {v.baseImage}{v.overlayImage ? ` + ${v.overlayImage}` : ''}</div>
          ))}
        </div>
      )}
      <Button variant="ghost" disabled={!derived} onClick={() => derived && onConvert(derived.printAreas, derived.mockupViews)}>
        Convert to print areas
      </Button>
    </div>
  );
}


// --- local field helpers ---
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <section><h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-subtle">{title}</h3>{children}</section>;
}
function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{children}</div>;
}
function Text({ label, value, onChange, placeholder, disabled }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">{label}</span>
      <input value={value} disabled={disabled} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px] disabled:opacity-60" />
    </label>
  );
}
function Num({ label, value, onChange }: { label: string; value: number | undefined; onChange: (v: number) => void }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">{label}</span>
      <input type="number" value={value ?? ''} onChange={(e) => onChange(Number(e.target.value))} className="mt-1 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px]" />
    </label>
  );
}
function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.04em] text-muted">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px]">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
